import { createTheme, styled } from '@mui/material';
import { default as MuiBackDrop } from '@mui/material/Backdrop';
import type { ComponentProps } from 'react';

interface CustomLoaderProps {
  size?: 'sm' | 'md' | 'lg';
  animationdelay?: string;
}

const sizeToStyle = (theme: ReturnType<typeof createTheme>) => ({
  sm: {
    width: '1.5rem',
    height: '1.5rem',
    padding: theme.spacing(0.5),
  },
  md: {
    width: '3rem',
    height: '3rem',
    padding: theme.spacing(0.75),
  },
  lg: {
    width: '5rem',
    height: '5rem',
    padding: theme.spacing(1.25),
  },
});

const LoaderRoot = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== 'color' && prop !== 'variant' && prop !== 'sx',
  slot: 'Root',
  name: 'CustomLoader',
})<CustomLoaderProps>(({ theme, size = 'md', animationdelay }) => {
  return {
    aspectRatio: 1,
    borderRadius: '50%',
    ...sizeToStyle(theme)[size],
    background: theme.palette.primary.main,
    webkitMask:
      'conic-gradient(#0000 10%,#000),\n' +
      '    linear-gradient(#000 0 0) content-box',
    mask:
      'conic-gradient(#0000 10%,#000),\n' +
      '    linear-gradient(#000 0 0) content-box',
    webkitMaskComposite: 'source-out',
    maskComposite: 'subtract',
    animation: 'rotate 0.6s infinite linear',
    animationdelay: animationdelay,
    '@keyframes rotate': {
      to: {
        transform: 'rotate(1turn)',
      },
    },
  };
});

export function Loader(props: ComponentProps<typeof LoaderRoot>) {
  return <LoaderRoot role="progressbar" aria-label="Loading" {...props} />;
}

export default function Backdrop(
  props: React.ComponentProps<typeof MuiBackDrop> & {
    loaderProps?: CustomLoaderProps;
  }
) {
  const { loaderProps, ...rootProps } = props;

  return (
    <MuiBackDrop
      // MUI hides its backdrop from assistive tech because a modal sits on top
      // of it; here the backdrop is the content, so the loader must be exposed.
      aria-hidden={false}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
      {...rootProps}
    >
      <Loader {...loaderProps} />
    </MuiBackDrop>
  );
}

export function SuspenseFallback(props: CustomLoaderProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        padding: '48px 0',
      }}
    >
      <Loader animationdelay="100ms" {...props} />
    </div>
  );
}

export function LoadingOverlay(props: CustomLoaderProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        backdropFilter: 'blur(2px)',
      }}
    >
      <Loader animationdelay="100ms" {...props} />
    </div>
  );
}
